/**
 * Generic helper to wrap Capacitor plugin methods so that failed calls preserve a useful
 * call-site stack trace originating in application code rather than only showing the
 * internal bridge (e.g. user-script / CapacitorException frames).
 */
export class BridgeError extends Error {
    /** Original rejection value (if non-Error). */
    constructor(message: string, cause?: unknown) {
        super(message, { cause });
        this.name = 'BridgeError';
    }
}

const stripFirstLine = (stack?: string) => (stack ? stack.split('\n').slice(1).join('\n') : '');

// Methods that typically register listeners / are not expected to throw meaningful bridge errors.
const LISTENER_METHOD_NAMES = new Set(['addListener', 'removeListener', 'removeAllListeners']);

/** Determine if a value is Promise-like without forcing `instanceof` (works across realms). */
const isPromiseLike = (value: unknown): value is Promise<unknown> =>
    !!value && typeof (value as Promise<unknown>).then === 'function';

/** Build / adapt an error, wrapping non-Error values in BridgeError with a phase-specific message. */
const toBridgeError = (err: unknown, label: string, methodName: string, phase: 'failed' | 'threw'): Error => {
    if (err instanceof Error) return err;
    return new BridgeError(`${label}.${methodName} ${phase}`, err);
};

/**
 * Append (or assign) call-site stack frames to an error. Ensures we always retain call-site frames
 * even if the original error had no stack (bugfix vs earlier implementation which skipped fallback
 * for sync throws without an existing stack).
 */
const attachCallSite = (error: Error, callSite: Error, marker: string) => {
    if (!callSite.stack) return;
    if (error.stack) {
        error.stack += `\n-- ${marker} --\n` + stripFirstLine(callSite.stack);
    } else {
        error.stack = callSite.stack; // fallback when original error had no stack
    }
};

/** Wrap a single plugin method with stack enrichment semantics. */
const wrapBridgeMethod = (orig: unknown, label: string, methodName: string, target: unknown) => {
    if (typeof orig !== 'function') return orig;
    if (LISTENER_METHOD_NAMES.has(methodName)) return orig;
    return (...args: unknown[]) => {
        const callSite = new Error(`${label}.${methodName}`);
        try {
            const fn = orig as (...fnArgs: unknown[]) => unknown;
            const result = fn.apply(target, args);
            if (isPromiseLike(result)) {
                return result.catch((err: unknown) => {
                    const enriched = toBridgeError(err, label, methodName, 'failed');
                    attachCallSite(enriched, callSite, 'call site');
                    throw enriched;
                });
            }
            return result;
        } catch (err) {
            const enriched = toBridgeError(err, label, methodName, 'threw');
            attachCallSite(enriched, callSite, 'call site (sync)');
            throw enriched;
        }
    };
};

/**
 * Wrap a Capacitor plugin object in a Proxy to enrich Error stacks for async (Promise) methods.
 *
 * Behavior:
 *  - On each method invocation, capture a call-site Error before crossing the native bridge.
 *  - If the underlying method returns a Promise that rejects, append the call-site frames.
 *  - Non-Error rejection values are wrapped in a BridgeError preserving the original value.
 *  - Synchronous throws are also augmented.
 *
 * Results in more useful stack traces for debugging failed bridge calls.
 * Especially useful for error reporting in Rollbar.
 *
 * Note: This is not directly related to sourcemaps,
 * it's related to getting a useful stack trace AFTER using a sourcemap.
 *
 * T is any object; we wrap its function properties.
 */
export const withBridgedStackTrace = <T extends object>(plugin: T, label: string): T => {
    return new Proxy(plugin, {
        get(target, prop, receiver) {
            const orig = Reflect.get(target, prop, receiver) as unknown;
            const methodName = String(prop);
            if (typeof orig !== 'function') {
                return (plugin as unknown as Record<PropertyKey, unknown>)[prop];
            }
            return wrapBridgeMethod(orig, label, methodName, target);
        },
    });
};

export type { BridgeError as CapacitorBridgeError };
